import { isEmpty, isNaN, isNil } from "lodash";
import {
  COMMON_LABELS,
  DivisionDict,
  getDivisionLabel,
} from "../components/default-constants";

/**
 *
 * @param label
 * @param labelArgs
 * @returns フォーマットされた文言
 */
export const formatLabel = (label: string, labelArgs: unknown[]): string => {
  let newLabel = label;
  for (const [i, arg] of labelArgs.entries()) {
    const regExp = new RegExp(`\\{${i}\\}`, "g");
    newLabel = newLabel.replace(regExp, arg as string);
  }

  return newLabel;
};

/**
 * 日時文字列をyyyy/MM/dd形式に変換して返却する
 * @param {string | null | undefined} 日時文字列
 * @return {string} yyyy/MM/dd形式の日付
 */
export const formatDate = (dateTimeStr?: string | null) => {
  if (isEmpty(dateTimeStr)) {
    return "";
  }

  const date = new Date(dateTimeStr!);
  if (isNaN(date.getTime)) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  // TODO なぜreplaceしているかは不明
  return `${year}/${month}/${day}`.replace(/\n|\r/g, "");
};

/**
 * 日時文字列を日付(yyyy-MM-dd)に変換する
 * @param 日時文字列
 * @return 日付(yyyy-MM-dd)
 */
export const formatDateHyphen = (dateTimeStr: string): string => {
  let date = new Date(dateTimeStr);
  let formatted = `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`.replace(
    /\n|\r/g,
    ""
  );
  return formatted;
};

/**
 * 日時文字列を日時(yyyy/MM/dd mm:ss)に変換して返却する.
 *
 * BEの時間がUTCのためそれをJSTに変換する処理を追加
 * @param {string} dateTimeStr 日時文字列
 * @return {string} 日時(yyyy/MM/dd mm:ss形式)
 */
export const formatDateTime = (dateTimeStr: string): string => {
  let date = new Date(dateTimeStr);
  date.setTime(date.getTime() + 1000 * 60 * 60 * 9); // JSTに変換
  let formatted = `${date.getFullYear()}/${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")} ${date
    .getHours()
    .toString()
    .padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`.replace(/\n|\r/g, "");
  return formatted;
};

/**
 * 日付と時間を日時（yyyy/MM/dd mm:ss形式）に結合して返却する.
 *
 * 日付または時間がfalse値の場合、それぞれ空文字として扱う。
 * @param {string | undefined} dateStr 日付文字列
 * @param {string | undefined} timeStr 時間文字列
 * @return 日時（yyyy/MM/dd mm:ss形式）
 */
export const fixedFormatDateTime = (
  dateStr: string | undefined,
  timeStr: string | undefined
): string => {
  const formattedDate = dateStr ? formatDate(dateStr) : "";
  const formattedTime = timeStr ?? "";

  return `${formattedDate} ${formattedTime}`;
};

/**
 * 日時文字列を日付(yyyy/MM/dd)に変換する
 * BEの時間がUTCのためそれをJSTに変換する処理を追加
 * @param 日時文字列
 * @return 日付(yyyy/MM/dd)
 */
export const formatYearDate = (dateTimeStr: string): string => {
  let date = new Date(dateTimeStr);

  date.setTime(date.getTime() + 1000 * 60 * 60 * 9); // JSTに変換
  let formatted = `${date.getFullYear()}/${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}`.replace(
    /\n|\r/g,
    ""
  );
  return formatted;
};

/**
 * 日時文字列(2023-04-14T10:31:13.484853+09:00 JSTタイムゾーン付き)を日付(yyyy/MM/dd mm:ss))に変換する
 *
 * @param 日時文字列
 * @return 日付(yyyy/MM/dd mm:ss)
 */
export const formatDateTimeWithoutConvert = (dateTimeStr: string): string => {
  const date = new Date(dateTimeStr);

  // toISOString()で、UTC時間になってしまう為、UTCとタイムゾーンとの差を取得し、ミリ秒に変換
  const diff = date.getTimezoneOffset() * 60 * 1000;
  date.setTime(date.getTime() - diff);

  const formatted = date
    .toISOString()
    .replace("T", " ")
    .replaceAll("-", "/")
    .substring(0, 16);

  return formatted;
};

/**
 * 小数値(0.1234)を百分率(12.34%))に変換する
 * 小数第三位の値で切り上げする
 * %を追加する
 *
 * @param 小数値(0.xxxx)
 * @return 百分率(xx.yy%)
 */
export const formatPercentage = (rateNumber: number): string => {
  let formatted = "";
  let formattedNum: number;
  if (rateNumber !== null) {
    formattedNum = rateNumber * 100;
    formatted = formattedNum.toFixed(2) + "%";
  }

  return formatted;
};

/**
 * 百分率(12.34))を小数値(0.1234)に変換する
 * 小数第五位の値で切り上げする
 *
 * @param 百分率(xx.yy)
 * @return 小数値(0.xxxx)
 */
export const formatDecimal = (rateNumber: number): number => {
  let formatted = 0;
  if (rateNumber !== null) {
    formatted = rateNumber / 100;
    formatted = Number(formatted.toFixed(4));
  }

  return formatted;
};

/**
 * 小数値(0.1234)を百分率(12.34%))に変換する
 * 小数第三位の値で切り上げする
 *
 * @param 小数値(0.xxxx)
 * @return 百分率(xx.yy%)
 */
export const formatMultiplication = (rateNumber: number): string => {
  let formatted = "";
  let formattedNum: number;
  if (rateNumber !== null) {
    formattedNum = rateNumber * 100;
    formatted = formattedNum.toFixed(2);
  }

  return formatted;
};

/**
 * 郵便番号を画面表示用に変換して返却する.
 * @param {string | null | undefined} postalCode 郵便番号
 * @returns {string} 画面表示用の郵便番号
 */
export const changeStyle = (postalCode?: string | null) => {
  if (isEmpty(postalCode) || !/^\d{7}$/.test(postalCode!)) {
    return "";
  }

  const firstPart = postalCode!.slice(0, 3);
  const secondPart = postalCode!.slice(3, 7);

  return `〒${firstPart}-${secondPart}`;
};

/**
 * 三桁区切り
 */
export const change3Digits = (item: number): string => {
  let strItem = "";
  if (item !== null) {
    strItem = item.toLocaleString("ja-JP");
  }
  return strItem;
};

/**
 * 指定した文字数まで全角スペースで文字列を埋める.
 * @param {string} str 埋める前の文字列
 * @param {string} targetLength 目標の文字数
 * @returns {string} 全角スペースで埋められた文字列
 */
export const padWithFullWidthSpace = (
  str: string,
  targetLength: number
): string => {
  const currentLength = str.length;

  if (currentLength < targetLength) {
    return str + "　".repeat(targetLength - currentLength);
  }

  return str;
};

/**
 * 受け取った文字列の先頭と末尾に丸括弧を付けて返却する.
 *
 * @param {string} str 文字列
 * @returns {string} 丸括弧が付いた文字列
 */
export const wrapWithParentheses = (str: string): string => {
  if (isEmpty(str)) {
    return "";
  }

  return `${COMMON_LABELS.LEFT_PARENTHESIS}${str}${COMMON_LABELS.RIGHT_PARENTHESIS}`;
};

/**
 * 文字列の配列を指定の区切りで連結した文字列に変換する.
 * nullやundefinedの値は除外し、空文字列も除外する。
 * @param list 変換対象の文字列の配列
 * @param delimiter 区切り文字
 * @returns 区切られた文字列
 */
export const joinStrings = (
  list: (string | null | undefined)[],
  delimiter: string
): string => {
  if (isEmpty(list)) {
    return "";
  } else {
    return list
      .map((item) => String(item).trim())
      .filter((item) => !isNil(item) && item !== "")
      .join(delimiter);
  }
};

/**
 * 文字列のコード配列を対応するラベルに変換し、"/" 区切りで返却する.
 * 受け取ったコード配列が空であればハイフンを返却する.
 *
 * @param {DivisionDict[]} codeList コードリスト
 * @param {string[]} codes 文字列のコード配列
 * @returns {string} 変換されたラベル文字列
 */
export const formatLabelsFromCodes = (
  codeList: DivisionDict[],
  codes: string[]
): string => {
  const labels = codes.map((code) => getDivisionLabel(codeList, code));
  return labels.length === 0 ? COMMON_LABELS.HYPHEN : labels.join("/");
};
